import { useState, createRef, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { DropzoneRef, DropzoneFileRejection, useNotification, useBaseHistory, useModuleRoute } from 'spindl-shared-components';
import { NOTIFICATION_DURATION } from '../../../lib/constants';

import { getTranslation } from '../../../lib/reactUtils';
import { DropzoneErrorEnum, DropzoneStateEnum } from '../../../lib/types';
import { isEmptyString } from '../../../lib/utils';
import useGetAccount from '../../../modules/auth/useGetAccount';
import { CSVRowValueProps } from '../../molecules/CSVRow';
import { CSVUser, UploadCSVCombinedProps, UploadCSVStateEnum } from './types';
import { getAllRolesUseCase, getAllAccountsUseCase, sendAccountInvitationEmailsUseCase } from './UploadCSV.interactor';
import { parseUsersFromCSV } from './utils';

const usePresenter = (props: UploadCSVCombinedProps): UploadCSVCombinedProps => {
  const { t } = useTranslation();
  const history = useBaseHistory();
  const { communicationUrl } = useModuleRoute();
  const { account } = useGetAccount();
  const { trigger } = useNotification();

  const { mutateAsync: sendBulkInvites, isLoading: isSendingInvites } = useMutation(sendAccountInvitationEmailsUseCase);
  const { data: roles } = useQuery(['getAllRoles'], async () => getAllRolesUseCase());
  const { data: accounts } = useQuery(['getAllAccounts'], async () => getAllAccountsUseCase());

  const [dropZoneError, setDropZoneError] = useState<DropzoneErrorEnum>('None');
  const [dropZoneState, setDropZoneState] = useState<DropzoneStateEnum>('Default');
  const [uploadCSVState, setUploadCSVState] = useState<UploadCSVStateEnum>('Upload');

  const [uploadedFileName, setUploadedFileName] = useState<string | undefined>(undefined);
  const [CSVUsers, setCSVUsers] = useState<CSVUser[]>([]);
  const [selectedCSVUsers, setSelectedCSVUsers] = useState<CSVUser[]>([]);

  const dropzoneRef = createRef<DropzoneRef>();

  useEffect(() => {
    setSelectedCSVUsers(CSVUsers.filter(user => user.selected));
  }, [CSVUsers]);

  const handleReplaceFile = () => {
    setDropZoneState('Default');
    setDropZoneError('None');
    setUploadCSVState('Upload');
    setCSVUsers([]);
  };

  const handleCloseWizard = () => {
    history.replace('/');
  };

  const handleBackButton = () => {
    switch (uploadCSVState) {
      case 'Uploaded':
        handleReplaceFile();
        break;
    
      default:
        history.replace('/wizard/downloadCSV');
        break;
    }
  };

  const handleBrowseButton = () => {
    dropzoneRef.current?.open();
  };

  const processCSVContent = (content: string) => {
    try {
      const users = parseUsersFromCSV(accounts || [], roles || [], content, ',');
      setCSVUsers(users);
      setUploadCSVState('Uploaded');
    } catch {
      setDropZoneError('Error');
      setDropZoneState('Default');
    }
  };

  const onFileDrop = (acceptedFiles: File[], fileRejections: DropzoneFileRejection[]) => {
    if (fileRejections.length > 0) {
      setDropZoneError('Error');
      return;
    }
    setDropZoneState(acceptedFiles.length === 1 ? 'Loading' : 'Default');
    if (acceptedFiles.length === 1) {
      const file = acceptedFiles[0];
      setUploadedFileName(file.name);
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target && e.target.result) {
          const content = e.target.result;
          const isContentString = typeof content === 'string';
          const csvContent = isContentString ? content : Buffer.from(content).toString();
          processCSVContent(csvContent);
        }
      };
      reader.readAsText(file);
    }
  };

  const onDragEnter = () => {
    setDropZoneState('Dragging');
  };

  const onDragLeave = () => {
    setDropZoneState('Default');
  };

  const showNotification = () => {
    if (selectedCSVUsers.length) {
      const message = selectedCSVUsers.length === 1
        ? getTranslation('uploadCSV.single_invite_notification', {
          invitationEmail: selectedCSVUsers[0]['Email address (Mandatory)'] || '',
        })
        : getTranslation('uploadCSV.multiple_invite_notification', {
          totalInvitationEmails: selectedCSVUsers.length,
        });
      trigger({
        duration: NOTIFICATION_DURATION,
        message,
        type: 'Positive',
      });
    }
  };

  const handleIndividualCheckboxClick = (index: number) => {
    const checkedUser = CSVUsers[index];
    checkedUser.selected = !checkedUser.selected;
    CSVUsers.splice(index, 1, checkedUser);

    setCSVUsers([...CSVUsers]);
  };

  const handleHeaderCheckboxClick = () => {
    const isChecked = selectedCSVUsers.length === CSVUsers.length;
    setCSVUsers(CSVUsers.map((user) => {
      return {
        ...user,
        selected: !isChecked,
      };
    }));
  };

  const handleSendEmailInvites = async () => {
    if (selectedCSVUsers.length && account) {
      const bulkInvitations = selectedCSVUsers.map((selectedUser) => {
        return {
          email: selectedUser['Email address (Mandatory)'],
          firstName: selectedUser['First Name'],
          lastName: selectedUser['Last Name'],
          roleId: roles?.find((role) => role.roleName === selectedUser.Role)?.id,
        };
      });
      await sendBulkInvites({
        senderFirstName: account.firstName,
        senderLastName: account.lastName,
        senderEmail: account.email,
        invitations: bulkInvitations,
      });
      showNotification();
      handleCloseWizard();
    }
  };

  let CSVUserList;
  if (CSVUsers.length) {
    CSVUserList = CSVUsers.map((user, userIndex): CSVRowValueProps => {
      const fullName = `${user['First Name'] || ''} ${user['Last Name'] || ''}`;
      return {
        checkbox: {
          targetProps: {
            checkbox: {
              icon: {
                asset: user.selected ? 'CheckBoxFilled' : 'Checkbox',
              },
              onClick: () => {
                handleIndividualCheckboxClick(userIndex);
              },
            },
          },
        },
        email: {
          targetProps: {
            text: {
              value: user['Email address (Mandatory)'],
            },
          },
        },
        name: {
          targetProps: {
            text: {
              value: isEmptyString(fullName) ? t('default.name') : fullName,
            },
          },
        },
        role: {
          targetProps: {
            text: {
              value: user.Role || t('default.role'),
            },
          },
        },
      };
    });
  }

  return {
    ...props,
    state: uploadCSVState,
    stepLabelList: {
      stepLabels: [
        {
          state: 'Completed',
          styleType: 'Default',
          stepNumber: {
            text: {
              value: '1',
            },
          },
          text: {
            value: t('step_labels.select_method'),
          },
        },
        {
          state: 'Completed',
          styleType: 'Default',
          stepNumber: {
            text: {
              value: '2',
            },
          },
          text: {
            value: t('step_labels.download_template'),
          },
        },
        {
          state: 'Active',
          styleType: 'End',
          stepNumber: {
            text: {
              value: '3',
            },
          },
          text: {
            value: t('step_labels.invite_users'),
          },
        },
      ],
    },
    button: {
      onClick: handleCloseWizard,
    },
    label: {
      value: dropZoneError === 'Error'
        ? t('uploadCSV.add_users_from_csv')
        : t('uploadCSV.import_users_from_csv'),
    },
    instructions: {
      value: getTranslation(
        dropZoneError === 'Error'
          ? 'uploadCSV.error_instructions'
          : 'uploadCSV.instructions',
      ),
    },
    dropzone: {
      maxFiles: 1,
      onDrop: onFileDrop,
      accept: {
        'text/CSV': ['.csv'],
      },
      ref: dropzoneRef,
      noClick: true,
      onDragEnter,
      onDragLeave,
      contentProps: {
        state: dropZoneState,
        error: dropZoneError,
        instructions: {
          value: t('uploadCSV.drag_instruction'),
        },
        oR: {
          value: t('uploadCSV.or'),
        },
        button: {
          text: {
            value: t('button.browse'),
          },
          onClick: handleBrowseButton,
        },
        text: {
          value: t('uploadCSV.error'),
        },
      },
    },
    backButton: {
      text: {
        value: t('button.back'),
      },
      onClick: handleBackButton,
    },
    sendButton: {
      text: {
        value: t('button.send_invite_emails', {
          validChipCount: selectedCSVUsers.length < 2 ? '' : selectedCSVUsers.length,
          plural: selectedCSVUsers.length === 1 ? '' : 's',
        }),
      },
      disabled: selectedCSVUsers.length === 0,
      onClick: handleSendEmailInvites,
      state: isSendingInvites ? 'Loading' : 'Default',
    },
    cSVTable: {
      header: {
        checkbox: {
          targetProps: {
            checkbox: {
              icon: {
                asset: CSVUsers.length === selectedCSVUsers.length
                  ? 'CheckBoxFilled' : 'Checkbox',
              },
              onClick: handleHeaderCheckboxClick,
            },
          },
        },
        email: {
          targetProps: {
            text: {
              value: t('uploadCSV.user_table_headers.email'),
            },
          },
        },
        name: {
          targetProps: {
            text: {
              value: t('uploadCSV.user_table_headers.name'),
            },
          },
        },
        role: {
          targetProps: {
            text: {
              value: t('uploadCSV.user_table_headers.role'),
            },
          },
        },
      },
      cSVRowList: {
        cSVRows: CSVUserList,
      },
    },
    fileAction: {
      button: {
        text: {
          value: t('button.replace_file'),
        },
        onClick: handleReplaceFile,
      },
      icon: {
        asset: 'Document',
      },
      text: {
        value: uploadedFileName,
      },
    },
    reviewUsersLabel: {
      value: t('uploadCSV.review_users'),
    },
    inviteEmailLabel: {
      value: t('uploadCSV.invite_email'),
    },
    reviewUsersText: {
      value: 
      <Trans key='invite_users.review_users_text'>
        {t('uploadCSV.review_users_text.the')} 
        <Link to={`${communicationUrl}/editor/sign-up-invite`} target='_blank'>
          {t('uploadCSV.review_users_text.tag')}
        </Link>
        {t('uploadCSV.review_users_text.content')} 
      </Trans>,
    },
  };
};

export default usePresenter;
